import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { POLL_INTERVAL_OPTIONS, emptyMailbox } from "./helpers";

export default function MailboxDialog({
  open,
  mailbox,
  saving,
  onClose,
  onSave,
}) {
  const [form, setForm] = useState(emptyMailbox());

  useEffect(() => {
    if (!open) return;
    if (mailbox) {
      setForm({
        ...emptyMailbox(),
        ...mailbox,
        imap_password: "",
        imap_port: mailbox.imap_port ?? 993,
        poll_interval_sec: Number(mailbox.poll_interval_sec) || 1800,
      });
    } else {
      setForm(emptyMailbox());
    }
  }, [open, mailbox]);

  const patch = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{mailbox?.id ? "Edit mailbox" : "Add mailbox"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!form.enabled}
                onChange={(e) => patch("enabled", e.target.checked)}
              />
            }
            label="Enable inbound email processing"
          />
          <TextField
            label="Label"
            size="small"
            fullWidth
            value={form.name || ""}
            onChange={(e) => patch("name", e.target.value)}
            placeholder="IndiaMART sales"
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="IMAP host / domain"
              size="small"
              fullWidth
              placeholder="imap.gmail.com"
              value={form.imap_host || ""}
              onChange={(e) => patch("imap_host", e.target.value)}
            />
            <TextField
              label="Port"
              size="small"
              sx={{ width: { xs: "100%", sm: 120 } }}
              value={form.imap_port ?? ""}
              onChange={(e) => patch("imap_port", e.target.value)}
            />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              label="Username"
              size="small"
              fullWidth
              value={form.imap_username || ""}
              onChange={(e) => patch("imap_username", e.target.value)}
            />
            <TextField
              label="Password"
              size="small"
              fullWidth
              type="password"
              placeholder={
                mailbox?.imap_password_set ? "Saved — leave blank to keep" : ""
              }
              value={form.imap_password || ""}
              onChange={(e) => patch("imap_password", e.target.value)}
              helperText={
                mailbox?.imap_password_set
                  ? "Password is saved. Enter a new value only to change it."
                  : "App password recommended (Gmail/Outlook)."
              }
            />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <TextField
              label="Mailbox folder"
              size="small"
              sx={{ width: { xs: "100%", sm: 200 } }}
              value={form.imap_mailbox || "INBOX"}
              onChange={(e) => patch("imap_mailbox", e.target.value)}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.imap_secure !== false}
                  onChange={(e) => patch("imap_secure", e.target.checked)}
                />
              }
              label="Use TLS/SSL (port 993)"
            />
            <TextField
              label="Default country code"
              size="small"
              sx={{ width: { xs: "100%", sm: 160 } }}
              value={form.default_country_code || "91"}
              onChange={(e) => patch("default_country_code", e.target.value)}
            />
          </Stack>
          <FormControl size="small" sx={{ maxWidth: 280 }}>
            <InputLabel>Auto-poll every</InputLabel>
            <Select
              label="Auto-poll every"
              value={Number(form.poll_interval_sec) || 1800}
              onChange={(e) => patch("poll_interval_sec", Number(e.target.value))}
            >
              {POLL_INTERVAL_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={saving}
          onClick={() => onSave(form)}
        >
          {saving ? "Saving…" : mailbox?.id ? "Save mailbox" : "Add mailbox"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
